import { graphqlQuery } from "@/lib/api/graphql";
import type { PromiseUnifiedActivity } from "./usePromiseRecentActivities";
import type { GraphQLCursorConnection } from "@/lib/graphql-pagination";
import {
	ACTIVITY_TYPE_TO_TAG,
	ACTIVITY_TYPE_TO_TYPE,
	parseActivityOutcome,
} from "@/lib/activity-type-maps";
import { useCursorPagination } from "./useCursorPagination";

// --- GraphQL types ---

interface PromiseRecentActivityGraphQLNode {
	activityId: number;
	activityType: string;
	title: string;
	description: string;
	metadataJson: string | null;
	actorName: string;
	createdAt: string;
}

interface PromiseRecentActivityListGraphQLResponse {
	promisesToPay: {
		promiseRecentActivityList: GraphQLCursorConnection<PromiseRecentActivityGraphQLNode>;
	};
}

// --- GraphQL query ---

const PROMISE_RECENT_ACTIVITY_LIST_QUERY = `
  query PromiseRecentActivityList($first: Int, $after: String, $filter: PromiseRecentActivityListFilterInput) {
    promisesToPay {
      promiseRecentActivityList(first: $first, after: $after, filter: $filter) {
        edges {
          node {
            activityId
            activityType
            title
            description
            metadataJson
            actorName
            createdAt
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

const ACTIVITY_PAGE_SIZE = 25;

// --- Mapping ---

function mapGraphQLNodeToPromiseUnifiedActivity(
	node: PromiseRecentActivityGraphQLNode,
): PromiseUnifiedActivity {
	const tag = ACTIVITY_TYPE_TO_TAG[node.activityType] ?? "Activity";
	const type = (ACTIVITY_TYPE_TO_TYPE[node.activityType] ?? "system") as PromiseUnifiedActivity["type"];

	return {
		id: String(node.activityId),
		type,
		tag,
		title: node.title,
		description: node.description,
		outcome: parseActivityOutcome(node.metadataJson),
		createdBy: node.actorName,
		createdAt: new Date(node.createdAt),
	};
}

// --- Mock data ---

function buildMockActivities(): PromiseUnifiedActivity[] {
	const items: PromiseUnifiedActivity[] = [
		{
			id: "mock-action-1",
			type: "status_change",
			tag: "Activity",
			title: "Promise status changed to Active",
			description: "Promise status changed to Active",
			createdBy: "Sarah Johnson",
			createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
		},
		{
			id: "mock-action-2",
			type: "call",
			tag: "Communication",
			title: "Outbound call made to customer",
			description: "Outbound call made to customer",
			outcome: "Customer confirmed payment commitment",
			createdBy: "Sarah Johnson",
			createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
		},
		{
			id: "mock-action-3",
			type: "email",
			tag: "Communication",
			title: "Payment reminder email sent",
			description: "Payment reminder email sent",
			outcome: "Email delivered successfully",
			createdBy: "System",
			createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
		},
		{
			id: "mock-note-1",
			type: "note",
			tag: "Note",
			title: "Note Added",
			description:
				"Initial promise recorded via phone call. Customer confirmed payment date.",
			createdBy: "Sarah Johnson",
			createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
		},
		{
			id: "mock-note-2",
			type: "note",
			tag: "Note",
			title: "Note Added",
			description:
				"Follow-up call made. Customer reconfirmed commitment to pay on promised date.",
			createdBy: "Sarah Johnson",
			createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
		},
		{
			id: "mock-note-3",
			type: "note",
			tag: "Note",
			title: "Note Added",
			description:
				"Reminder email sent to customer with payment details and invoice copies.",
			createdBy: "System",
			createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
		},
	];
	return items.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

// --- Hook ---

export interface PromiseRecentActivitiesGraphQLResult {
	activities: PromiseUnifiedActivity[];
	isLoading: boolean;
	isFetchingMore: boolean;
	hasMore: boolean;
	loadMore: () => void;
	reset: () => void;
	refetch: () => void;
}

export function usePromiseRecentActivitiesGraphQL(
	promiseId: string | undefined,
): PromiseRecentActivitiesGraphQLResult {
	const result = useCursorPagination<PromiseRecentActivityGraphQLNode, PromiseUnifiedActivity>(
		{
			pageSize: ACTIVITY_PAGE_SIZE,
			buildMockData: buildMockActivities,
			executeQuery: async (apiConfig, afterCursor, signal) => {
				if (!promiseId) {
					return { edges: [], pageInfo: { endCursor: null, hasNextPage: false } };
				}
				const variables: Record<string, unknown> = {
					first: ACTIVITY_PAGE_SIZE,
					filter: { promiseToPayId: { eq: Number(promiseId) } },
				};
				if (afterCursor) {
					variables.after = afterCursor;
				}
				const response =
					await graphqlQuery<PromiseRecentActivityListGraphQLResponse>(
						apiConfig,
						PROMISE_RECENT_ACTIVITY_LIST_QUERY,
						variables,
						signal,
					);
				return response.promisesToPay.promiseRecentActivityList;
			},
			mapNode: mapGraphQLNodeToPromiseUnifiedActivity,
			postProcess: (items) =>
				items.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
		},
		[promiseId],
	);

	return {
		activities: result.items,
		isLoading: result.isLoading,
		isFetchingMore: result.isFetchingMore,
		hasMore: result.hasMore,
		loadMore: result.loadMore,
		reset: result.reset,
		refetch: result.refetch,
	};
}
